import { SHADOW_RESET_CSS, empty, createElement } from './main.js';
import { NO_CHILD_ELEMENTS } from './chunk-widgets.js';

const DEFAULT_EVENTS = [
    // mouse
    'click',
    'dblclick',
    'mouseup',
    'mousedown',
    'mousemove',
    'mouseover',
    'mouseout',
    'wheel',
    // touch
    'touchstart',
    'touchmove',
    'touchend',
    // keyboard
    'keydown',
    'keypress',
    'keyup',
];
/**
 * Stop bubbling events from one DOM element.
 *
 * @param element - The DOM Element from wich stop the event propagation.
 * @param ...eventNames[] - events to stop.
 */
function preventEventsBubbling(element, ...eventNames) {
    const eventsToStop = eventNames.length ? eventNames : DEFAULT_EVENTS;
    eventsToStop.forEach((key) => {
        element.addEventListener(key, (ev) => {
            ev.stopPropagation();
        });
    });
}

const createRootContainer = (options, services = {}) => {
    const { id: dataId, class: dataClass, zIndex, topElementStyles, contentStyles, } = options;
    const { preventEventsBubbling: preventEventsBubbling$1 = preventEventsBubbling, document = globalThis.document, } = services;
    const defaultCreateElement = (...args) => createElement(args[0], args[1], document);
    const { createElement: createElement$1 = defaultCreateElement, } = services;
    // create container element
    const customAttributes = topElementStyles
        ? {
            ariaLive: 'polite',
            id: dataId,
            class: dataClass,
        }
        : {
            id: dataId,
            ariaLive: 'assertive',
            style: {
                'z-index': zIndex || 'auto',
                position: 'fixed',
            },
        };
    const topElement = createElement$1('div', customAttributes);
    // stop events bubbling on root element
    // to avoid errors on client page listeners
    preventEventsBubbling$1(topElement);
    const shadowRoot = topElement.attachShadow({ mode: 'closed' });
    const shadowEl = createElement$1('div', { class: dataId });
    shadowRoot.appendChild(shadowEl);
    if (topElementStyles) {
        // Apply the style as a string
        topElement.setAttribute('style', Object.keys(topElementStyles).map((key) => `${key}:${topElementStyles[key]}`).join(';'));
    }
    // Attach the styles to the shadow root
    const styleEl = createElement$1('style');
    const effectiveContentStyles = [
        SHADOW_RESET_CSS,
        contentStyles,
    ].filter((item) => !empty(item)).join('\n');
    if (styleEl.styleSheet) {
        styleEl.styleSheet.cssText = effectiveContentStyles;
    }
    else {
        styleEl.appendChild(document.createTextNode(effectiveContentStyles));
    }
    shadowRoot.appendChild(styleEl);
    return {
        shadowEl, topElement, shadowRoot,
    };
};

/**
 * Check if a given DOM Node is contained in a document.
 *
 * @param node - The Node we want to check.
 * @param docNodeRef - Pass the document node reference to check against.
 *  When this parameter is missing, then this function will check
 *  if the element is contained in *any* document.
 */
function isInDocument(node, docNodeRef) {
    if (typeof docNodeRef?.contains === 'function') {
        return docNodeRef.contains(node);
    }
    // This loop will stop once we reach the top of the hierarchy
    while (node.parentNode) {
        // eslint-disable-next-line no-param-reassign
        node = node.parentNode;
    }
    // The first ancestor should be of nodeType 9 (document)
    return node?.nodeType === 9;
}

const insertRootContainer = (reference, topElement, position, isInDocument$1 = isInDocument) => {
    let effectivePosition = position;
    // validate position
    // Case 1: check if the position to insert is "inside" and if the reference element is a childless element
    if (effectivePosition === 'II' && NO_CHILD_ELEMENTS.includes(reference.tagName)) {
        effectivePosition = 'IB';
    }
    if (effectivePosition === 'IB') {
        reference.parentNode?.insertBefore(topElement, reference);
    }
    else if (effectivePosition === 'IA') {
        reference.parentNode?.insertBefore(topElement, reference.nextSibling);
    }
    else if (effectivePosition === 'II') {
        reference.insertBefore(topElement, reference.firstChild);
    }
    const documentReference = reference.getRootNode();
    // check injetion
    if (!isInDocument$1(topElement, documentReference)) {
        return false;
    }
    return true;
};

export { createRootContainer, insertRootContainer, isInDocument };
